import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AcademicCapIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PencilAltIcon,
  UserIcon,
  XCircleIcon,
} from "@heroicons/react/solid";
import UsersList from "../components/UsersList";
import ModernDatePicker from "../components/ModernDatePicker";
import { FiPlus, FiSave, FiSend, FiTrash2 } from "react-icons/fi";
import { apiFetch } from "../utils/apiClient";
import { showError } from "../components/ErrorPopup";
import { useNavigate } from "react-router-dom";
import {
  findFormationMileageRate,
  calculateFormationTransport,
} from "../utils/formationMileage";

const inputClassName =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-3 text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const labelClassName = "block text-[15px] font-medium text-slate-700";

const sectionCardClassName =
  "mt-5 w-full rounded-xl border border-gray-200 bg-white shadow-sm [&>div>*]:max-w-5xl";

function InlineError({ message }) {
  if (!message) return null;

  return (
    <p className="mt-1 flex items-center gap-1.5 text-sm text-red-500" role="alert">
      <span className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full bg-red-100 text-[10px] font-bold text-red-500">
        !
      </span>
      {message}
    </p>
  );
}

const COST_OPTIONS = [
  { value: "inscription", label: "Frais d’inscription" },
  { value: "transport", label: "Transport" },
  { value: "hebergement", label: "Hébergement" },
  { value: "repas", label: "Repas" },
  { value: "suppleance", label: "Suppléance (235 $ par jour)" },
  { value: "autre", label: "Autre dépense" },
];

const COST_VALUES_TO_CLEAR = {
  inscription: ["coutInscription"],
  transport: ["kilometrageTransport"],
  hebergement: ["coutHebergement"],
  repas: ["coutRepas"],
  suppleance: ["coutSuppleance"],
  autre: ["autreDescription", "coutAutre"],
};

const MONEY_COST_FIELDS = {
  inscription: "coutInscription",
  hebergement: "coutHebergement",
  repas: "coutRepas",
  suppleance: "coutSuppleance",
};

const inferSelectedCosts = (data) => {
  if (Array.isArray(data?.coutsSelectionnes) && data.coutsSelectionnes.length) {
    const allowedCosts = new Set(COST_OPTIONS.map(({ value }) => value));
    return [...new Set(data.coutsSelectionnes)].filter((cost) =>
      allowedCosts.has(cost),
    );
  }

  return [
    data?.coutInscription !== "" && data?.coutInscription != null
      ? "inscription"
      : null,
    (data?.kilometrageTransport !== "" &&
      data?.kilometrageTransport != null) ||
    (data?.coutTransport !== "" && data?.coutTransport != null)
      ? "transport"
      : null,
    data?.coutHebergement !== "" && data?.coutHebergement != null
      ? "hebergement"
      : null,
    data?.coutRepas !== "" && data?.coutRepas != null ? "repas" : null,
    data?.coutSuppleance !== "" && data?.coutSuppleance != null
      ? "suppleance"
      : null,
    (data?.autreDescription?.trim() ||
      (data?.coutAutre !== "" && data?.coutAutre != null))
      ? "autre"
      : null,
  ].filter(Boolean);
};

const generateNumeroFormulaire = () => {
  const date = new Date();
  const yy = date.getFullYear().toString().slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const randomNumber = Math.floor(1000 + Math.random() * 9000);

  return `CSV-${yy}${mm}${dd}${randomNumber}-PF`;
};

function SectionHeader({ icon: Icon, iconColor = "text-blue-400", children }) {
  return (
    <div className="mt-8 w-full rounded-t-xl bg-gradient-to-r from-[#182a43] to-[#40516a] py-3 shadow-sm">
      <p className="ml-4 flex items-center text-base font-semibold tracking-wide text-white">
        <Icon className={`h-4 w-4 ${iconColor} mr-2`} aria-hidden="true" />
        {children}
      </p>
    </div>
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  min,
  step,
  required = false,
  disabled = false,
  readOnly = false,
  error = "",
}) {
  return (
    <div className="w-full min-w-0">
      <label htmlFor={name} className={labelClassName}>
        {label}
        {required && <span className="text-xl text-red-900"> *</span>} :
      </label>
      <input
        id={name}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        min={min}
        step={step}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        className={`${inputClassName} ${
          disabled || readOnly ? "bg-slate-50 text-slate-700" : "bg-white"
        } ${error ? "border-red-400 focus:border-red-500 focus:ring-red-100" : ""}`}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${name}-error` : undefined}
      />
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function ReadOnlyInfo({ label, value }) {
  return (
    <div className="min-w-0 py-2">
      <p className="text-[13px] font-semibold uppercase tracking-wide text-slate-600">
        {label}
      </p>
      <p
        className="mt-1 break-words text-[15px] font-medium text-slate-900"
        title={value}
      >
        {value || "—"}
      </p>
    </div>
  );
}

function YesNoField({
  label,
  name,
  value,
  onChange,
  required = false,
  error = "",
}) {
  return (
    <div className="w-full min-w-0">
      <div className="grid grid-row-2 items-center gap-4">
        <label className={labelClassName}>
          {label}
          {required && <span className="text-xl text-red-900"> *</span>} :
        </label>
        <div
          className="flex flex-wrap items-center gap-3"
          role="radiogroup"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
        >
          {["oui", "non"].map((option) => {
            const isSelected = value === option;

            return (
              <label
                key={option}
                className={`flex min-w-24 items-center justify-center rounded-lg border px-6 py-2.5 text-sm font-medium cursor-pointer transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:ring-offset-1 ${
                  isSelected
                    ? "bg-blue-100 text-blue-600 border-blue-300"
                    : "bg-white text-gray-500 border-gray-300 hover:border-gray-400 hover:text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name={name}
                  value={option}
                  checked={isSelected}
                  onChange={onChange}
                  className="sr-only"
                  required={required}
                />
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </label>
            );
          })}
        </div>
      </div>
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function MoneyField({
  label,
  name,
  value,
  onChange,
  required = false,
  error = "",
  hideLabelOnDesktop = false,
}) {
  const handleAmountChange = (event) => {
    const normalizedValue = event.target.value
      .replace(",", ".")
      .replace(/[^0-9.]/g, "");
    const [integerPart, ...decimalParts] = normalizedValue.split(".");
    const amount =
      decimalParts.length > 0
        ? `${integerPart}.${decimalParts.join("")}`
        : integerPart;

    onChange({ target: { name, value: amount } });
  };

  return (
    <div className="w-full min-w-0">
      <label
        htmlFor={name}
        className={`block text-[15px] font-medium leading-5 text-slate-700 ${
          hideLabelOnDesktop ? "lg:sr-only" : ""
        }`}
      >
        {label}
        {required && <span className="text-red-700"> *</span>} :
      </label>
      <div className="relative mt-1.5 w-full">
        <input
          id={name}
          type="text"
          name={name}
          value={value}
          onChange={handleAmountChange}
          inputMode="decimal"
          required={required}
          pattern={required ? "^(?=.*[1-9])\\d*(?:\\.\\d+)?$" : undefined}
          title={required ? "Le montant doit être supérieur à zéro." : undefined}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${name}-error` : undefined}
          className={`h-10 w-full rounded-lg border bg-white py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 shadow-sm outline-none transition-all duration-200 focus:ring-2 ${
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-100"
              : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
          }`}
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
          $
        </span>
      </div>
      <div id={`${name}-error`}>
        <InlineError message={error} />
      </div>
    </div>
  );
}

function ParticipationFormation({ user, enabled, checkGroupAdmin }) {
  const [selectedForm, setSelectedForm] = useState(null);
  const [formId, setFormId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [couleur, setCouleur] = useState("");
  const [errors, setErrors] = useState({});
  const [commentaire, setCommentaire] = useState("");
  const [costRows, setCostRows] = useState([]);
  const hasCostDetails = costRows.some(
    ({ type }) => type === "transport" || type === "autre",
  );
  const nextCostRowId = useRef(0);
  const formRef = useRef(null);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const numeroFormulaire = params.get("numeroFormulaire");
  const page = params.get("page");

  const [formData, setFormData] = useState({
    numeroFormulaire: generateNumeroFormulaire(),
    dateSoumission: new Date().toISOString().split("T")[0],
    userId: "",
    nom: "",
    courriel: "",
    matricule: "",
    lieuTravail: "",
    poste: "",
    managerInfo: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
      etatDemande: "",
    },
    directionSecteurSurintendance: {
      nom: "",
      email: "",
      userId: "",
      matricule: "",
    },
    descriptionObjectifs: "",
    ville: "",
    provincePays: "",
    dateDebut: "",
    nombreJours: "",
    voyageExterieurOntario: "",
    suppleanceRequise: "",
    nombreJoursSuppleance: "",
    suppleanceFondsCentraux: "",
    justificationFondsCentraux: "",
    coutInscription: "",
    coutsSelectionnes: [],
    kilometrageTransport: "",
    coutHebergement: "",
    coutRepas: "",
    coutSuppleance: "",
    autreDescription: "",
    coutAutre: "",
    signatureElectronique: "non",
    etatDemande: "",
    traitant: [],
  });

  const [mileageRates, setMileageRates] = useState([]);
  const [mileageStatus, setMileageStatus] = useState("loading");

  useEffect(() => {
    const controller = new AbortController();
    const fetchMileageRates = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/TauxKilometrage`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error("Mileage rates unavailable");
        const rates = await response.json();
        if (!Array.isArray(rates)) throw new Error("Invalid mileage rates");
        if (!controller.signal.aborted) {
          setMileageRates(rates);
          setMileageStatus("ready");
        }
      } catch (error) {
        if (!controller.signal.aborted) setMileageStatus("error");
      }
    };
    fetchMileageRates();
    return () => controller.abort();
  }, []);

  const mileageRate = findFormationMileageRate(
    mileageRates,
    formData.dateDebut,
  );
  const coutTransport = calculateFormationTransport(
    formData.kilometrageTransport,
    mileageRate,
  );
  const transportPending =
    formData.kilometrageTransport !== "" && coutTransport === "";
  const mileageMessage = !formData.dateDebut
    ? "Sélectionnez la date de début pour connaître le taux applicable."
    : mileageStatus === "loading"
      ? "Chargement du taux kilométrique…"
      : mileageStatus === "error"
        ? "Impossible de charger les taux. Rechargez la page pour réessayer."
        : mileageRate === null
          ? "Aucun taux kilométrique n’est défini pour cette date de début."
          : `Taux applicable : ${new Intl.NumberFormat("fr-CA", {
              maximumFractionDigits: 6,
            }).format(mileageRate)} $/km — selon la date de début.`;

  useEffect(() => {
    const displayName = user?.displayName || "";

    setFormData((current) => ({
      ...current,
      userId: current.userId || user?.id || "",
      nom: current.nom || displayName,
      courriel: current.courriel || user?.mail || user?.userPrincipalName || "",
      matricule: current.matricule || user?.employeeId || "",
      lieuTravail: current.lieuTravail || user?.officeLocation || "",
      poste: current.poste || user?.jobTitle || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!numeroFormulaire || !page || !user?.id) return;

    const controller = new AbortController();

    const fetchForm = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_API_BASE_URL}/selectedFormParticipationFormation?numeroFormulaire=${encodeURIComponent(numeroFormulaire)}&page=${encodeURIComponent(page)}`,
          { signal: controller.signal },
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || result.message);
        }

        if (controller.signal.aborted) return;

        const selectedCosts = inferSelectedCosts(result);
        setSelectedForm(result);
        setFormId(result._id);
        setFormData((current) => ({
          ...current,
          ...result,
          coutsSelectionnes: selectedCosts,
        }));
        setCostRows(
          selectedCosts.map((type) => ({
            id: `cost-${nextCostRowId.current++}`,
            type,
          })),
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error(error);
          showError(error);
        }
      }
    };

    fetchForm();
    return () => controller.abort();
  }, [numeroFormulaire, page, user?.id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const costAmountFields = new Set([
      "coutInscription",
      "kilometrageTransport",
      "coutHebergement",
      "coutRepas",
      "coutSuppleance",
      "coutAutre",
    ]);

    setFormData((current) => {
      const updated = { ...current, [name]: value };

      if (name === "suppleanceRequise" && value === "non") {
        updated.nombreJoursSuppleance = "";
        updated.suppleanceFondsCentraux = "non";
        updated.justificationFondsCentraux = "";
        updated.coutSuppleance = "";
      }

      if (name === "suppleanceFondsCentraux" && value === "non") {
        updated.justificationFondsCentraux = "";
      }

      return updated;
    });

    setErrors((current) => {
      if (
        !current[name] &&
        name !== "kilometrageTransport" &&
        !(costAmountFields.has(name) && current.totalEstime)
      ) {
        return current;
      }
      return {
        ...current,
        [name]: "",
        ...(name === "kilometrageTransport" ? { coutTransport: "" } : {}),
        ...(costAmountFields.has(name) ? { totalEstime: "" } : {}),
      };
    });
  };

  const renderError = (field) => <InlineError message={errors[field]} />;

  const addCostRow = () => {
    setCostRows((current) => {
      if (
        current.some(({ type }) => !type) ||
        current.length >= COST_OPTIONS.length
      ) {
        return current;
      }

      return [
        ...current,
        { id: `cost-${nextCostRowId.current++}`, type: "" },
      ];
    });
  };

  const updateSelectedCosts = (rows, typeToClear = "") => {
    setFormData((current) => {
      const updated = { ...current };

      (COST_VALUES_TO_CLEAR[typeToClear] || []).forEach((field) => {
        updated[field] = "";
      });

      updated.coutsSelectionnes = rows
        .map(({ type }) => type)
        .filter(Boolean);
      return updated;
    });
  };

  const handleCostTypeChange = (rowId, type) => {
    setCostRows((current) => {
      const previousType = current.find(({ id }) => id === rowId)?.type || "";
      const updatedRows = current.map((row) =>
        row.id === rowId ? { ...row, type } : row,
      );
      updateSelectedCosts(updatedRows, previousType);
      return updatedRows;
    });

    setErrors((current) => {
      const updated = { ...current, [`costType-${rowId}`]: "" };
      const previousType = costRows.find(({ id }) => id === rowId)?.type;
      (COST_VALUES_TO_CLEAR[previousType] || []).forEach((field) => {
        updated[field] = "";
      });
      return updated;
    });
  };

  const removeCostRow = (rowId) => {
    setCostRows((current) => {
      const removedType = current.find(({ id }) => id === rowId)?.type || "";
      const updatedRows = current.filter(({ id }) => id !== rowId);
      updateSelectedCosts(updatedRows, removedType);
      return updatedRows;
    });

    setErrors((current) => {
      const updated = { ...current };
      const removedType = costRows.find(({ id }) => id === rowId)?.type;
      delete updated[`costType-${rowId}`];
      (COST_VALUES_TO_CLEAR[removedType] || []).forEach((field) => {
        delete updated[field];
      });
      if (removedType === "transport") delete updated.coutTransport;
      delete updated.totalEstime;
      return updated;
    });
  };

  const getSelectedCostErrors = () => {
    const nextErrors = {};
    const isPositive = (value) =>
      value !== "" && Number.isFinite(Number(value)) && Number(value) > 0;

    costRows.forEach((row) => {
      if (!row.type) {
        nextErrors[`costType-${row.id}`] =
          "Veuillez sélectionner un type de coût.";
        return;
      }

      const moneyFieldName = MONEY_COST_FIELDS[row.type];
      if (moneyFieldName && !isPositive(formData[moneyFieldName])) {
        nextErrors[moneyFieldName] =
          "Le montant doit être supérieur à zéro.";
      }

      if (row.type === "transport") {
        if (!isPositive(formData.kilometrageTransport)) {
          nextErrors.kilometrageTransport =
            "Le kilométrage doit être supérieur à zéro.";
        } else if (!isPositive(coutTransport)) {
          nextErrors.coutTransport =
            "Le montant du transport n’a pas pu être calculé.";
        }
      }

      if (row.type === "autre") {
        if (!formData.autreDescription?.trim()) {
          nextErrors.autreDescription = "La description est obligatoire.";
        }
        if (!isPositive(formData.coutAutre)) {
          nextErrors.coutAutre = "Le montant doit être supérieur à zéro.";
        }
      }
    });

    return nextErrors;
  };

  const validateForm = () => {
    const nextErrors = getSelectedCostErrors();
    const isPositive = (value) =>
      value !== "" && Number.isFinite(Number(value)) && Number(value) > 0;

    if (!isPositive(total)) {
      nextErrors.totalEstime =
        "Le total estimé doit être supérieur à zéro.";
    }

    if (!formData.managerInfo?.userId) {
      nextErrors.managerInfo =
        "Veuillez sélectionner la supervision immédiate.";
    }
    if (!formData.directionSecteurSurintendance?.userId) {
      nextErrors.directionSecteurSurintendance =
        "Veuillez sélectionner la direction de service / secteur / surintendance.";
    }
    if (!formData.descriptionObjectifs?.trim()) {
      nextErrors.descriptionObjectifs =
        "Veuillez inscrire une courte description et les objectifs.";
    }
    if (!formData.ville?.trim()) {
      nextErrors.ville = "Veuillez inscrire la ville.";
    }
    if (!formData.provincePays?.trim()) {
      nextErrors.provincePays = "Veuillez inscrire la province ou le pays.";
    }
    if (!formData.dateDebut) {
      nextErrors.dateDebut = "Veuillez sélectionner la date de début.";
    }
    if (!isPositive(formData.nombreJours)) {
      nextErrors.nombreJours =
        "Le nombre de jours doit être supérieur à zéro.";
    }
    if (!["oui", "non"].includes(formData.voyageExterieurOntario)) {
      nextErrors.voyageExterieurOntario = "Veuillez sélectionner Oui ou Non.";
    }
    if (!["oui", "non"].includes(formData.suppleanceRequise)) {
      nextErrors.suppleanceRequise = "Veuillez sélectionner Oui ou Non.";
    }
    if (formData.suppleanceRequise === "oui") {
      if (!isPositive(formData.nombreJoursSuppleance)) {
        nextErrors.nombreJoursSuppleance =
          "Le nombre de jours de suppléance doit être supérieur à zéro.";
      }
      if (!["oui", "non"].includes(formData.suppleanceFondsCentraux)) {
        nextErrors.suppleanceFondsCentraux =
          "Veuillez sélectionner Oui ou Non.";
      }
      if (
        formData.suppleanceFondsCentraux === "oui" &&
        !formData.justificationFondsCentraux?.trim()
      ) {
        nextErrors.justificationFondsCentraux =
          "Veuillez inscrire une justification.";
      }
    }
    if (formData.signatureElectronique !== "oui") {
      nextErrors.signatureElectronique =
        "Veuillez confirmer que les informations fournies sont exactes, complètes et vérifiées.";
    }

    setErrors(nextErrors);

    const firstInvalidField = Object.keys(nextErrors)[0];
    if (firstInvalidField) {
      window.requestAnimationFrame(() => {
        const errorElement = document.getElementById(
          `${firstInvalidField}-error`,
        );
        const fieldElement = document.getElementById(firstInvalidField);
        (errorElement || fieldElement)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleCertificationChange = (event) => {
    setFormData((current) => ({
      ...current,
      signatureElectronique: event.target.checked ? "oui" : "non",
    }));
    setErrors((current) => ({ ...current, signatureElectronique: "" }));
  };

  const handleUserSelect = (selectedUser, fieldName) => {
    setFormData((current) => ({
      ...current,
      [fieldName]: {
        nom: selectedUser.displayName,
        email: selectedUser.mail,
        userId: selectedUser.id,
        matricule: selectedUser.employeeId,
      },
    }));
    setErrors((current) => ({ ...current, [fieldName]: "" }));
  };

  const total = useMemo(() => {
    return [
      "coutInscription",
      "coutHebergement",
      "coutRepas",
      "coutSuppleance",
      "coutAutre",
    ].reduce(
      (sum, field) => {
        const amount = Number.parseFloat(formData[field]);
        return sum + (Number.isFinite(amount) ? amount : 0);
      },
      Number(coutTransport || 0),
    );
  }, [formData, coutTransport]);

  const formattedTotal = new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
  }).format(total);

  const handleFormAction = async (event, actionType, updatedTraitant = null) => {
    event.preventDefault();

    const updatedFormData = {
      ...formData,
      dateSoumission: new Date(),
      etatDemande: actionType,
      coutTransport: Number(coutTransport || 0),
      totalEstime: total,
      traitant: updatedTraitant ?? formData.traitant ?? [],
      action: actionType,
    };

    const method = formId ? "PUT" : "POST";
    const url = formId
      ? `${process.env.REACT_APP_API_BASE_URL}/form/${formId}?form=ParticipationFormation`
      : `${process.env.REACT_APP_API_BASE_URL}/form?form=ParticipationFormation`;

    setIsSubmitting(true);
    setMessage("");

    try {
      const response = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedFormData),
      });
      const result = await response.json();

      if (!response.ok) {
        if (result.errors) setErrors(result.errors);
        const validationMessage = result.errors
          ? Object.values(result.errors)[0]
          : null;
        throw new Error(validationMessage || result.error || result.message);
      }

      if (result.form?._id) setFormId(result.form._id);
      setErrors({});
      setFormData((current) => ({
        ...current,
        etatDemande: actionType,
        traitant: updatedFormData.traitant,
      }));

      const feedbackByAction = {
        Brouillon: {
          message:
            "Succès ! Le formulaire a été enregistré. Veuillez noter que l’enregistrement ne signifie pas que le formulaire a été soumis.",
          couleur: "text-gray-700",
        },
        "En traitement": {
          message: "Succès ! La demande est maintenant en traitement.",
          couleur: "text-green-600",
        },
        Approuvé: {
          message: "Succès ! Le formulaire a été approuvé.",
          couleur: "text-green-600",
        },
        Rejeté: {
          message: "Le formulaire a été rejeté.",
          couleur: "text-red-700",
        },
      };
      const feedback = feedbackByAction[actionType];

      if (feedback) {
        setMessage(feedback.message);
        setCouleur(feedback.couleur);
        setIsSubmitted(true);
        await new Promise((resolve) => window.setTimeout(resolve, 3000));
        setIsSubmitted(false);
      }

      if (actionType === "Brouillon") {
        return;
      }

      if (page === "Mes taches") {
        if (window.opener && !window.opener.closed) {
          window.close();
        } else {
          navigate("/taches", { state: { selectedForm: null } });
        }
      } else {
        navigate("/demande", { state: { selectedForm: null } });
      }
    } catch (error) {
      console.error(error);
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = (event) => {
    handleFormAction(event, "Brouillon");
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const updatedTraitant = [
      ...(formData.traitant || []),
      {
        userId: formData.managerInfo.userId,
        nom: formData.managerInfo.nom,
        email: formData.managerInfo.email,
        matricule: formData.managerInfo.matricule,
        commentaire: "",
        etatDemande: "En traitement",
        dateHeure: new Date(),
      },
    ];

    handleFormAction(event, "En traitement", updatedTraitant);
  };

  const addTraitantEntry = (etatDemande) => [
    ...(formData.traitant || []),
    {
      userId: user?.id || "",
      nom: user?.displayName || "",
      email: user?.mail || user?.userPrincipalName || "",
      matricule: user?.employeeId || "",
      commentaire,
      etatDemande,
      dateHeure: new Date(),
    },
  ];

  const handleApprouve = (event) => {
    handleFormAction(event, "Approuvé", addTraitantEntry("Approuvé"));
  };

  const handleRejeter = (event) => {
    handleFormAction(event, "Rejeté", addTraitantEntry("Rejeté"));
  };

  const lastTraitant = formData.traitant?.at(-1);
  const canProcess =
    page === "Mes taches" &&
    formData.etatDemande === "En traitement" &&
    (lastTraitant?.userId === user?.id || checkGroupAdmin);
  const isReadOnly =
    page === "Mes taches" ||
    (page === "Mes demandes" &&
      Boolean(numeroFormulaire) &&
      (!selectedForm ||
        !["Brouillon", "Rejeté"].includes(formData.etatDemande)));
  const commentaires = (formData.traitant || []).filter((traitant) =>
    traitant.commentaire?.trim(),
  );

  if (
    (!enabled && !selectedForm && page !== "Mes taches") ||
    (!enabled &&
      selectedForm &&
      selectedForm.etatDemande === "Brouillon" &&
      page !== "Mes taches")
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
        <XCircleIcon className="h-16 w-16 text-red-500 mb-4 animate-pulse" />
        <h3 className="text-2xl font-bold text-red-600 mb-2">
          Formulaire de participation à une formation fermé
        </h3>
        <p className="text-gray-700 max-w-md">
          Le formulaire est désormais fermé.
        </p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#f7f7f8] px-4 py-6 sm:px-6 lg:px-8">
      <form
        ref={formRef}
        onSubmit={(event) => event.preventDefault()}
        className="mx-auto w-full max-w-[1600px]"
      >
        <fieldset disabled={isReadOnly} className="contents">
        <dl className="ml-auto grid w-full max-w-md grid-cols-1 rounded-lg border border-gray-200 bg-white text-sm sm:w-fit sm:max-w-none sm:grid-cols-[auto_auto]">
          <div className="min-w-0 px-4 py-3 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">
              Numéro de formulaire
            </dt>
            <dd className="mt-1 break-words font-semibold text-slate-800">
              {formData.numeroFormulaire}
            </dd>
          </div>
          <div className="min-w-0 border-t border-gray-100 px-4 py-3 sm:border-l sm:border-t-0 sm:px-5">
            <dt className="text-xs font-medium text-slate-500">Date</dt>
            <dd className="mt-1 font-medium text-slate-700">
              <time dateTime={formData.dateSoumission}>
                {new Intl.DateTimeFormat("fr-CA", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                  timeZone: "UTC",
                }).format(new Date(formData.dateSoumission))}
              </time>
            </dd>
          </div>
        </dl>

        <div className="my-6 flex w-full justify-center">
          <div className="w-fit max-w-full rounded-lg border border-blue-100 border-l-4 border-l-blue-500 bg-blue-50 px-4 py-3 text-blue-800 shadow-sm">
            <p className="flex items-center gap-2 text-lg font-semibold">
              <AcademicCapIcon className="h-5 w-5 flex-none text-blue-600" />
              Demande de participation à une formation
            </p>
          </div>
        </div>

        <SectionHeader icon={UserIcon}>
          Information du membre du personnel
        </SectionHeader>

        <div className={sectionCardClassName}>
          <div className="p-5 sm:p-6">
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-800">
                Renseignements du profil
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Ces informations proviennent de votre compte professionnel.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <ReadOnlyInfo label="Nom" value={formData.nom} />
              <ReadOnlyInfo label="Courriel" value={formData.courriel} />
              <ReadOnlyInfo
                label="Lieu de travail"
                value={formData.lieuTravail}
              />
              <ReadOnlyInfo label="Poste" value={formData.poste} />
            </div>
          </div>

          <div className="rounded-b-xl border-t border-gray-200 bg-white p-5 sm:p-6">
            <div className="mb-5">
              <p className="text-sm font-semibold text-gray-800">
                Responsables de l’approbation
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Recherchez et sélectionnez les personnes responsables.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="w-full min-w-0" id="managerInfo">
                <label className="mb-2 block text-[15px] font-medium text-slate-700">
                  Supervision immédiate
                  <span className="ml-1 text-red-600">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.managerInfo.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(selectedUser, "managerInfo")
                      }
                      initialValue={formData.managerInfo.email}
                    />
                  )}

                  {formData.managerInfo.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.managerInfo.nom.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.managerInfo.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.managerInfo.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            managerInfo: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la supervision immédiate"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                <div id="managerInfo-error">
                  {renderError("managerInfo")}
                </div>
              </div>

              <div
                className="w-full min-w-0"
                id="directionSecteurSurintendance"
              >
                <label className="mb-2 block text-[15px] font-medium text-slate-700">
                  Direction de service / secteur / surintendance
                  <span className="ml-1 text-red-600">*</span>
                </label>
                <div className="relative [&_input]:text-[15px] [&_input::placeholder]:text-slate-500 [&_input::placeholder]:opacity-100">
                  {!formData.directionSecteurSurintendance.nom && (
                    <UsersList
                      onUserSelect={(selectedUser) =>
                        handleUserSelect(
                          selectedUser,
                          "directionSecteurSurintendance",
                        )
                      }
                      initialValue={
                        formData.directionSecteurSurintendance.email
                      }
                    />
                  )}

                  {formData.directionSecteurSurintendance.nom && (
                    <div className="flex min-h-[62px] items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-600">
                        {formData.directionSecteurSurintendance.nom
                          .charAt(0)
                          .toUpperCase()}
                      </div>
                      <div className="flex min-w-0 flex-col">
                        <span className="break-words text-[15px] font-semibold text-slate-900">
                          {formData.directionSecteurSurintendance.nom}
                        </span>
                        <span className="break-words text-[13px] leading-5 text-slate-600">
                          {formData.directionSecteurSurintendance.email}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setFormData((current) => ({
                            ...current,
                            directionSecteurSurintendance: {
                              nom: "",
                              email: "",
                              userId: "",
                              matricule: "",
                            },
                          }))
                        }
                        className="ml-auto flex h-8 w-8 flex-none items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label="Retirer la direction sélectionnée"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                <div id="directionSecteurSurintendance-error">
                  {renderError("directionSecteurSurintendance")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader icon={AcademicCapIcon}>
          Description de la sortie
        </SectionHeader>

        <div className={sectionCardClassName}>
          <div className="space-y-5 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Renseignements sur la formation
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Précisez les objectifs, le lieu et la durée de la formation.
              </p>
            </div>
            <div>
              <label htmlFor="descriptionObjectifs" className={labelClassName}>
                Courte description et objectifs
                <span className="text-xl text-red-900"> *</span> :
              </label>
              <textarea
                id="descriptionObjectifs"
                name="descriptionObjectifs"
                value={formData.descriptionObjectifs}
                onChange={handleChange}
                rows={5}
                required
                className={`${inputClassName} resize-y`}
                aria-invalid={Boolean(errors.descriptionObjectifs)}
                aria-describedby={
                  errors.descriptionObjectifs
                    ? "descriptionObjectifs-error"
                    : undefined
                }
              />
              <div id="descriptionObjectifs-error">
                {renderError("descriptionObjectifs")}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
              <Field
                label="Ville"
                name="ville"
                value={formData.ville}
                onChange={handleChange}
                required
                error={errors.ville}
              />
              <Field
                label="Province ou pays"
                name="provincePays"
                value={formData.provincePays}
                onChange={handleChange}
                required
                error={errors.provincePays}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
              <div className="min-w-0 text-[15px]">
                <p className={labelClassName}>
                  Date de début<span className="text-xl text-red-900"> *</span>{" "}
                  :
                </p>
                <ModernDatePicker
                  value={formData.dateDebut}
                  onChange={(date) =>
                    handleChange({ target: { name: "dateDebut", value: date } })
                  }
                  ariaLabel="Date de début de la formation (obligatoire)"
                />
                <div id="dateDebut-error">{renderError("dateDebut")}</div>
              </div>
              <Field
                label="Nombre de jours"
                name="nombreJours"
                value={formData.nombreJours}
                onChange={handleChange}
                type="number"
                min="1"
                required
                error={errors.nombreJours}
              />
            </div>

            <div>
              <YesNoField
                label="La formation nécessite-t-elle un voyage à l’extérieur de l’Ontario?"
                name="voyageExterieurOntario"
                value={formData.voyageExterieurOntario}
                onChange={handleChange}
                required
                error={errors.voyageExterieurOntario}
              />
            </div>
          </div>

          <div className="space-y-5 rounded-b-xl border-t border-gray-200 p-5 sm:p-6">
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Besoins de suppléance
              </p>
              <p className="mt-1 text-[13px] leading-5 text-slate-600">
                Indiquez si un remplacement est nécessaire pendant votre
                absence.
              </p>
            </div>
            <div>
              <YesNoField
                label="Suppléance requise"
                name="suppleanceRequise"
                value={formData.suppleanceRequise}
                onChange={handleChange}
                required
                error={errors.suppleanceRequise}
              />
            </div>

            {formData.suppleanceRequise === "oui" && (
              <>
                <div className="grid grid-cols-1 items-start gap-5 sm:grid-cols-2 sm:gap-6">
                  <Field
                    label="Nombre de jours de suppléance"
                    name="nombreJoursSuppleance"
                    value={formData.nombreJoursSuppleance}
                    onChange={handleChange}
                    type="number"
                    min="1"
                    required
                    error={errors.nombreJoursSuppleance}
                  />
                  <YesNoField
                    label="Suppléance payée de fonds centraux"
                    name="suppleanceFondsCentraux"
                    value={formData.suppleanceFondsCentraux}
                    onChange={handleChange}
                    required
                    error={errors.suppleanceFondsCentraux}
                  />
                </div>

                {formData.suppleanceFondsCentraux === "oui" && (
                  <div>
                    <label
                      htmlFor="justificationFondsCentraux"
                      className={labelClassName}
                    >
                      Justification pour une suppléance payée de fonds centraux
                      <span className="text-xl text-red-900"> *</span> :
                    </label>
                    <textarea
                      id="justificationFondsCentraux"
                      name="justificationFondsCentraux"
                      value={formData.justificationFondsCentraux}
                      onChange={handleChange}
                      rows={4}
                      required
                      className={`${inputClassName} resize-y`}
                      aria-invalid={Boolean(errors.justificationFondsCentraux)}
                      aria-describedby={
                        errors.justificationFondsCentraux
                          ? "justificationFondsCentraux-error"
                          : undefined
                      }
                    />
                    <div id="justificationFondsCentraux-error">
                      {renderError("justificationFondsCentraux")}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <SectionHeader icon={CurrencyDollarIcon} iconColor="text-yellow-400">
          Estimation des coûts
        </SectionHeader>

        <div className="mt-5 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="px-4 py-4 sm:px-5">
              <h3 className="text-[15px] font-semibold text-slate-900">
                Coûts prévus
              </h3>
              <p className="mt-1 text-[13px] text-slate-500">
                Ajoutez uniquement les catégories applicables à cette demande.
              </p>
            </div>

            {costRows.length === 0 && (
              <div className="border-t border-slate-200 px-4 py-7 text-center sm:px-5">
                <p className="text-sm font-medium text-slate-600">
                  Aucun coût ajouté.
                </p>
              </div>
            )}

            {costRows.length > 0 && (
            <div className="divide-y divide-slate-200 border-t border-slate-200">
              {costRows.map((row, index) => {
                const moneyFieldName = MONEY_COST_FIELDS[row.type];
                const selectedElsewhere = new Set(
                  costRows
                    .filter(({ id }) => id !== row.id)
                    .map(({ type }) => type),
                );

                return (
                  <div
                    key={row.id}
                    className={`grid grid-cols-1 items-start gap-x-5 gap-y-3 px-4 py-5 sm:px-5 ${
                      hasCostDetails
                        ? "lg:grid-cols-[minmax(0,320px)_minmax(0,320px)_minmax(0,200px)_40px]"
                        : "lg:grid-cols-[minmax(0,320px)_minmax(0,200px)_40px]"
                    }`}
                  >
                    <div className="min-w-0">
                      <label
                        htmlFor={`costType-${row.id}`}
                        className="block text-[15px] font-medium leading-5 text-slate-700"
                      >
                        Type de coût <span className="text-red-700">*</span> :
                      </label>
                      <select
                        id={`costType-${row.id}`}
                        value={row.type}
                        onChange={(event) =>
                          handleCostTypeChange(row.id, event.target.value)
                        }
                        required
                        className="mt-1.5 h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-[15px] text-slate-800 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      >
                        <option value="">Sélectionner un coût</option>
                        {COST_OPTIONS.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={selectedElsewhere.has(option.value)}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {renderError(`costType-${row.id}`)}
                    </div>

                    {(row.type === "transport" || row.type === "autre") && (
                    <div className="min-w-0 lg:col-start-2 lg:row-start-1">
                      {row.type === "transport" && (
                        <fieldset className="min-w-0">
                          <legend className="sr-only">Transport</legend>
                          <div className="w-full max-w-[200px]">
                            <label
                              htmlFor="kilometrageTransport"
                              className="block text-[15px] font-medium leading-5 text-slate-700"
                            >
                              Kilométrage <span className="text-red-700">*</span>
                            </label>
                            <div className="relative mt-1.5">
                              <input
                                id="kilometrageTransport"
                                name="kilometrageTransport"
                                type="text"
                                inputMode="decimal"
                                value={formData.kilometrageTransport}
                                aria-describedby="transportRate"
                                required
                                pattern="^(?=.*[1-9])\d*(?:\.\d+)?$"
                                title="Le kilométrage doit être supérieur à zéro."
                                onChange={(event) => {
                                  const value = event.target.value.replace(",", ".");
                                  if (/^\d*(\.\d*)?$/.test(value)) {
                                    handleChange({
                                      target: {
                                        name: "kilometrageTransport",
                                        value,
                                      },
                                    });
                                  }
                                }}
                                className={`h-10 w-full rounded-lg border bg-white py-2 pl-3 pr-10 text-right text-[15px] text-slate-800 shadow-sm outline-none focus:ring-2 ${
                                  errors.kilometrageTransport
                                    ? "border-red-400 focus:border-red-500 focus:ring-red-100"
                                    : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"
                                }`}
                              />
                              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-slate-500">
                                km
                              </span>
                            </div>
                            {renderError("kilometrageTransport")}
                          </div>
                          <p
                            id="transportRate"
                            role="status"
                            className="mt-2 text-[12px] leading-5 text-slate-500"
                          >
                            {mileageMessage}
                          </p>
                        </fieldset>
                      )}

                      {row.type === "autre" && (
                        <div>
                          <label
                            htmlFor="autreDescription"
                            className="block text-[15px] font-medium leading-5 text-slate-700"
                          >
                            Description <span className="text-red-700">*</span> :
                          </label>
                          <input
                            id="autreDescription"
                            name="autreDescription"
                            type="text"
                            value={formData.autreDescription}
                            onChange={handleChange}
                            required
                            aria-invalid={Boolean(errors.autreDescription)}
                            aria-describedby={errors.autreDescription ? "autreDescription-error" : undefined}
                            className={`mt-1.5 h-10 w-full rounded-lg border bg-white px-3 text-[15px] text-slate-800 shadow-sm outline-none focus:ring-2 ${errors.autreDescription ? "border-red-400 focus:border-red-500 focus:ring-red-100" : "border-slate-300 focus:border-blue-500 focus:ring-blue-100"}`}
                          />
                          <div id="autreDescription-error">
                            {renderError("autreDescription")}
                          </div>
                        </div>
                      )}

                    </div>
                    )}

                    <div className={`min-w-0 lg:row-start-1 ${hasCostDetails ? "lg:col-start-3" : "lg:col-start-2"}`}>
                      {moneyFieldName && (
                        <MoneyField
                          label="Montant"
                          name={moneyFieldName}
                          value={formData[moneyFieldName]}
                          onChange={handleChange}
                          required
                          error={errors[moneyFieldName]}
                        />
                      )}

                      {row.type === "transport" && (
                        <div>
                          <label
                            htmlFor="coutTransport"
                            className="block text-[15px] font-medium leading-5 text-slate-700"
                          >
                            Montant calculé :
                          </label>
                          <div className="relative mt-1.5">
                            <input
                              id="coutTransport"
                              type="text"
                              value={coutTransport}
                              readOnly
                              placeholder="—"
                              aria-describedby="transportRate"
                              className={`h-10 w-full rounded-lg border bg-slate-50 py-2 pl-3 pr-9 text-right text-[15px] text-slate-800 outline-none ${
                                errors.coutTransport
                                  ? "border-red-400"
                                  : "border-slate-200"
                              }`}
                            />
                            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                              $
                            </span>
                          </div>
                          {renderError("coutTransport")}
                        </div>
                      )}

                      {row.type === "autre" && (
                        <MoneyField
                          label="Montant"
                          name="coutAutre"
                          value={formData.coutAutre}
                          onChange={handleChange}
                          required
                          error={errors.coutAutre}
                        />
                      )}

                      {!row.type && (
                        <span className="hidden h-10 items-center text-slate-400 lg:flex">
                          —
                        </span>
                      )}
                    </div>

                    <div className={`flex min-h-10 items-center lg:row-start-1 lg:mt-[26px] lg:justify-center ${hasCostDetails ? "lg:col-start-4" : "lg:col-start-3"}`}>
                      {!isReadOnly && (
                        <button
                          type="button"
                          onClick={() => removeCostRow(row.id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                          aria-label={`Supprimer le coût ${index + 1}`}
                          title="Supprimer ce coût"
                        >
                          <FiTrash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {!isReadOnly && (
              <div className="border-t border-slate-200 p-3">
                <button
                  type="button"
                  onClick={addCostRow}
                  disabled={
                    costRows.some(({ type }) => !type) ||
                    costRows.length >= COST_OPTIONS.length
                  }
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 bg-blue-50/60 px-4 py-2 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FiPlus className="h-4 w-4" aria-hidden="true" />
                  Ajouter un coût
                </button>
              </div>
            )}

            <div
              id="totalEstime"
              className="border-t border-slate-200 bg-slate-50 px-4 py-4 sm:px-5"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm font-semibold text-slate-700">
                  {transportPending
                    ? "Total partiel (transport non calculé)"
                    : "Total estimé"}
                </p>
                <p
                  className={`text-xl font-bold ${
                    errors.totalEstime ? "text-red-600" : "text-slate-900"
                  }`}
                >
                  {formattedTotal}
                </p>
              </div>
              <div
                id="totalEstime-error"
                className="flex justify-end text-right"
              >
                {renderError("totalEstime")}
              </div>
            </div>
        </div>

        <SectionHeader icon={PencilAltIcon} iconColor="text-green-400">
          Signature du demandeur
        </SectionHeader>

        <div
          className={`${sectionCardClassName} p-5 sm:p-6`}
          id="signatureElectronique"
        >
          <label className="relative flex max-w-5xl cursor-pointer items-start gap-4 sm:items-center">
            <input
              type="checkbox"
              name="signatureElectronique"
              checked={formData.signatureElectronique === "oui"}
              onChange={handleCertificationChange}
              className="absolute opacity-0 w-6 h-6"
              required
            />
            <span
              className={`flex h-6 w-6 flex-none items-center justify-center rounded-md border-2 transition-all duration-200 ${
                formData.signatureElectronique === "oui"
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-300 bg-slate-50"
              }`}
            >
              {formData.signatureElectronique === "oui" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-blue-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </span>
            <span className="select-none text-sm font-medium leading-6 text-slate-900">
              Je certifie que les informations fournies sont exactes, complètes
              et vérifiées.
            </span>
          </label>
          <div id="signatureElectronique-error" className="max-w-5xl">
            {renderError("signatureElectronique")}
          </div>
        </div>
        </fieldset>

        {canProcess && (
          <div className="mt-8 w-full max-w-5xl rounded-xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <label
              htmlFor="commentaireParticipationFormation"
              className="block text-[15px] font-medium text-slate-700"
            >
              Commentaire :
            </label>
            <textarea
              id="commentaireParticipationFormation"
              name="commentaire"
              value={commentaire}
              onChange={(event) => setCommentaire(event.target.value)}
              rows={4}
              placeholder="Ajoutez un commentaire..."
              className={`${inputClassName} resize-y`}
            />
          </div>
        )}

        {selectedForm && (
          <section
            aria-labelledby="historique-commentaires-formation"
            className="mt-8 w-full max-w-5xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
          >
            <div className="border-b border-slate-100 px-4 py-4 sm:px-5">
              <h3
                id="historique-commentaires-formation"
                className="text-base font-semibold text-slate-900"
              >
                Historique des commentaires
                <span className="ml-2 font-normal text-slate-500">
                  ({commentaires.length})
                </span>
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Consultez les commentaires associés au traitement de cette
                demande.
              </p>
            </div>

            <div className="p-4 sm:p-5">
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[640px] table-fixed divide-y divide-slate-200 bg-white">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="w-1/4 p-3 text-left text-sm font-semibold text-slate-700">
                        Auteur
                      </th>
                      <th className="w-2/5 p-3 text-left text-sm font-semibold text-slate-700">
                        Commentaire
                      </th>
                      <th className="w-1/5 p-3 text-left text-sm font-semibold text-slate-700">
                        Date et heure
                      </th>
                      <th className="p-3 text-left text-sm font-semibold text-slate-700">
                        État
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {commentaires.map((traitant, index) => (
                      <tr
                        key={`${traitant.userId || "traitant"}-${traitant.dateHeure || index}`}
                        className="transition-colors hover:bg-slate-50"
                      >
                        <td className="break-words p-3 align-top">
                          <span className="block text-[15px] font-medium text-slate-800">
                            {traitant.nom || "—"}
                          </span>
                          {traitant.email && (
                            <span className="mt-1 block break-all text-xs text-slate-500">
                              {traitant.email}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-pre-wrap break-words p-3 align-top text-[15px] leading-6 text-slate-700">
                          {traitant.commentaire}
                        </td>
                        <td className="break-words p-3 align-top text-sm text-slate-500">
                          {traitant.dateHeure
                            ? new Intl.DateTimeFormat("fr-CA", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              }).format(new Date(traitant.dateHeure))
                            : "—"}
                        </td>
                        <td className="break-words p-3 align-top text-sm font-medium text-slate-700">
                          {traitant.etatDemande || "—"}
                        </td>
                      </tr>
                    ))}
                    {commentaires.length === 0 && (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-8 text-center text-sm text-slate-500"
                        >
                          Aucun commentaire ajouté à cette demande.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        <div className="mt-12 flex justify-start pb-4">
          <div className="flex flex-wrap gap-3">
            {page !== "Mes taches" &&
              ["", "Brouillon"].includes(formData.etatDemande) && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-slate-600 hover:bg-slate-600 active:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSave className="h-4 w-4 flex-none" aria-hidden="true" />
                  Enregistrer
                </button>
              )}
            {page !== "Mes taches" &&
              ["", "Brouillon", "Rejeté"].includes(
                formData.etatDemande,
              ) && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-green-800 hover:bg-green-800 active:bg-green-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FiSend className="h-4 w-4 flex-none" aria-hidden="true" />
                  Soumettre
                </button>
              )}
            {canProcess && (
              <>
                <button
                  type="button"
                  onClick={handleRejeter}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 min-w-24 items-center justify-center rounded-lg border border-red-700 bg-red-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Rejeter
                </button>
                <button
                  type="button"
                  onClick={handleApprouve}
                  disabled={isSubmitting}
                  className="inline-flex min-h-10 min-w-24 items-center justify-center rounded-lg border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:bg-green-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-700 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Approuver
                </button>
              </>
            )}
          </div>
        </div>
      </form>

      {isSubmitted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div
            className={`flex transform items-center space-x-3 rounded-lg bg-white px-5 py-4 ${couleur} shadow-xl transition-all duration-500 ease-in-out animate-bounce`}
            role="status"
            aria-live="polite"
          >
            <CheckCircleIcon
              className={`h-8 w-8 ${couleur} animate-pulse`}
              aria-hidden="true"
            />
            <span className="max-w-xl text-lg font-semibold">{message}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParticipationFormation;
